import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, ChevronRight } from "lucide-react";
import { useApp } from "../context/AppContext";
import { WhatsAppIcon, WHATSAPP_URL } from "../components/WhatsAppButton";

const PLACES = [
  {
    cat: "Palaces & Hôtels",
    title: "Suites d'exception",
    location: "Paris · Côte d'Azur · Genève",
    desc: "Une sélection de palaces et suites signature, entre service irréprochable et adresses confidentielles pour un séjour inoubliable.",
    image: "https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?crop=entropy&cs=srgb&fm=jpg&w=1000&q=85",
  },
  {
    cat: "Tables gastronomiques",
    title: "Restaurants étoilés",
    location: "Paris · Lyon · Monaco",
    desc: "Les tables les plus prisées, réservées aux fins gourmets : cuisine d'auteur, accords mets & vins et cadres d'exception.",
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?crop=entropy&cs=srgb&fm=jpg&w=1000&q=85",
  },
  {
    cat: "Mer & Croisières",
    title: "Yachts privés",
    location: "Méditerranée · Riviera",
    desc: "Naviguez en toute intimité le long des plus belles côtes, à bord de yachts d'exception avec équipage dédié.",
    image: "https://images.pexels.com/photos/13132149/pexels-photo-13132149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1000",
  },
  {
    cat: "Bien-être",
    title: "Spas & retraites privées",
    location: "Alpes · Provence · Suisse",
    desc: "Des parenthèses de sérénité absolue : spas privatifs, soins signature et retraites bien-être loin de l'agitation.",
    image: "https://images.unsplash.com/photo-1488345979593-09db0f85545f?crop=entropy&cs=srgb&fm=jpg&w=1000&q=85",
  },
  {
    cat: "Nuit & Art de vivre",
    title: "Rooftops & clubs privés",
    location: "Paris · Londres · Dubaï",
    desc: "Les adresses les plus élégantes pour vos soirées : rooftops avec vue, clubs privés et expériences sur mesure.",
    image: "https://images.unsplash.com/photo-1621275471769-e6aa344546d5?crop=entropy&cs=srgb&fm=jpg&w=1000&q=85",
  },
  {
    cat: "Shopping privé",
    title: "Boutiques & showrooms sur rendez-vous",
    location: "Paris · Milan",
    desc: "Un accès privilégié aux plus belles maisons, en séance privée, pour une expérience d'achat rien qu'à vous.",
    image: "https://images.unsplash.com/photo-1526743851649-2282229bac05?crop=entropy&cs=srgb&fm=jpg&w=1000&q=85",
  },
];

const VipPlaces = () => {
  const { t } = useApp();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="fade-up" data-testid="vip-page">
      {/* Hero */}
      <section className="relative h-[380px] md:h-[460px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1625513123245-fcb02d69ad12?crop=entropy&cs=srgb&fm=jpg&w=1800&q=85"
          alt="VIP"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1b1b1b]/55" />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-4 md:px-8 flex flex-col justify-center">
          <div className="h-px w-12 bg-[#b08d57] mb-5" />
          <p className="text-white/70 text-xs tracking-[0.2em] uppercase mb-3">Amourduluxe · Concierge</p>
          <h1 className="font-serif-lux text-4xl md:text-6xl text-white max-w-2xl leading-tight">Les endroits VIP à découvrir</h1>
          <p className="text-white/80 text-sm md:text-base mt-4 max-w-xl leading-relaxed">
            Notre sélection confidentielle d'adresses et d'expériences d'exception, pour vivre le luxe autrement.
          </p>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-10">
          <Link to="/" className="hover:text-[#b08d57]">{t("home")}</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-[#1b1b1b]">{t("vipNav")}</span>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
          {PLACES.map((p, i) => (
            <article key={i} className="group" data-testid={`vip-card-${i}`}>
              <div className="relative overflow-hidden rounded-sm aspect-[4/3]">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute top-3 left-3 bg-white/90 text-[#1b1b1b] text-[11px] tracking-wide uppercase px-3 py-1 rounded-sm">{p.cat}</span>
              </div>
              <h2 className="font-serif-lux text-2xl text-[#1b1b1b] mt-5">{p.title}</h2>
              <p className="flex items-center gap-1.5 text-xs text-[#b08d57] mt-1.5"><MapPin className="h-3.5 w-3.5" strokeWidth={1.5} /> {p.location}</p>
              <p className="text-[15px] text-[#555] leading-relaxed mt-3">{p.desc}</p>
            </article>
          ))}
        </div>

        {/* Concierge CTA */}
        <div className="mt-16 bg-[#1b1b1b] rounded-sm px-6 md:px-12 py-10 md:py-14 text-center">
          <div className="h-px w-10 bg-[#b08d57] mx-auto mb-5" />
          <h3 className="font-serif-lux text-3xl md:text-4xl text-white">Un service concierge sur mesure</h3>
          <p className="text-white/70 text-sm md:text-base mt-4 max-w-xl mx-auto leading-relaxed">
            Réservations, accès privilégiés, expériences privées : confiez-nous vos envies, nous nous occupons du reste.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="vip-whatsapp" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-7 py-3.5 text-sm tracking-[0.1em] uppercase hover:opacity-90 transition-opacity">
              <WhatsAppIcon className="h-4 w-4" /> WhatsApp
            </a>
            <Link to="/contact" data-testid="vip-contact" className="inline-flex items-center gap-2 border border-white/40 text-white px-7 py-3.5 text-sm tracking-[0.1em] uppercase hover:bg-white hover:text-[#1b1b1b] transition-colors">
              {t("contactNav")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VipPlaces;
